import React, { useCallback, useMemo, useState } from "react";
import { Box, IconButton } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Article } from "../types";
import { embedUrl, thumbnailCandidates } from "../utils/youtube";
import { surface } from "../theme";

interface Props {
  article: Article;
}

/**
 * 16:9 media slot for a card.
 *
 * Videos render as a static thumbnail ("facade") and only swap in the real
 * YouTube iframe once the viewer presses play. The previous build mounted a
 * live iframe per video card, which pulled the full YouTube player for every
 * video in the grid before the reader had clicked anything.
 */
export const ArticleMedia = React.memo(function ArticleMedia({ article }: Props) {
  const isVideo = article.type === "video" && !!article.videoId;
  const candidates = useMemo(
    () =>
      isVideo
        ? thumbnailCandidates(article.videoId)
        : [article.image].filter(Boolean),
    [isVideo, article.videoId, article.image]
  );

  const [candidateIndex, setCandidateIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const source = candidates[candidateIndex];
  const exhausted = candidateIndex >= candidates.length;

  /**
   * A cached image can finish before React attaches its handlers, so a failure
   * would never reach onError. Checking the node when it mounts closes that.
   */
  const attachImage = useCallback((node: HTMLImageElement | null) => {
    if (node && node.complete && node.naturalWidth === 0) {
      setCandidateIndex((index) => index + 1);
    }
  }, []);

  const frame = {
    position: "relative" as const,
    width: "100%",
    aspectRatio: "16 / 9",
    overflow: "hidden",
    backgroundColor: surface.raised,
    flexShrink: 0,
  };

  if (playing) {
    return (
      <Box sx={frame}>
        <Box
          component="iframe"
          src={embedUrl(article.videoId)}
          title={article.title}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
    );
  }

  return (
    <Box sx={frame}>
      {!exhausted && (
        <Box
          component="img"
          className="article-image"
          ref={attachImage}
          src={source}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          onError={() => setCandidateIndex((index) => index + 1)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            // Painted as it arrives, over the placeholder colour behind it.
            // Holding it at opacity 0 until the load event made a slow image
            // indistinguishable from a broken one — xtz.news serves ~886KB
            // covers, so those cards sat grey while everything else appeared.
            transition: "transform .35s ease",
          }}
        />
      )}

      {isVideo && (
        <IconButton
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${article.title}`}
          sx={{
            position: "absolute",
            inset: 0,
            margin: "auto",
            width: 56,
            height: 56,
            color: "#FFF",
            backgroundColor: "rgba(0,0,0,.55)",
            backdropFilter: "blur(2px)",
            border: "1px solid rgba(255,255,255,.25)",
            "&:hover": { backgroundColor: "#E62117", borderColor: "#E62117" },
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 34 }} />
        </IconButton>
      )}
    </Box>
  );
});
